import { Link } from 'react-router-dom';
import { Star, CheckCircle, Download, Badge, Percent, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PremiumBadge } from '@/components/ui/premium-badge';

/**
 * SubscriptionSuccess page displayed after a successful premium subscription checkout.
 * Shows welcome message and highlights the premium features now available.
 */
const SubscriptionSuccess = () => {
  const features = [
    {
      icon: Download,
      title: 'GPX Track Downloads',
      description: 'Download GPX tracks from any ride to use with your GPS device.',
    },
    {
      icon: Badge,
      title: 'Premium Badge',
      description: 'Show off your premium status with a badge on your profile.',
    },
    {
      icon: Percent,
      title: 'Rally Discounts',
      description: 'Get 10% off all rally ticket purchases.',
    },
    {
      icon: Star,
      title: 'Track Sharing',
      description: 'Share your GPX tracks with other premium members.',
    },
  ];

  return (
    <div className="min-h-screen bg-adv-dark pt-28 pb-16">
      <div className="container-custom max-w-2xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-adv-orange/20 mb-6">
            <Sparkles className="h-10 w-10 text-adv-orange" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">
            Welcome to Premium!
          </h1>
          <div className="flex items-center justify-center gap-2 mb-4">
            <PremiumBadge size="lg" showTooltip={false} />
          </div>
          <p className="text-adv-lightGray text-lg">
            You now have access to all premium features.
          </p>
        </div>

        {/* Features Grid */}
        <div className="glass-card rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Your Premium Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-3 p-4 rounded-lg bg-white/5"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-adv-orange/20 flex items-center justify-center">
                  <feature.icon className="h-5 w-5 text-adv-orange" />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">{feature.title}</h3>
                  <p className="text-sm text-adv-lightGray">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <Link to="/explore-rides" className="block">
            <Button className="w-full bg-adv-orange hover:bg-adv-orange/90">
              Explore Rides
            </Button>
          </Link>
          <Link to="/rallies" className="block">
            <Button variant="outline" className="w-full">
              Browse Rallies (10% Off!)
            </Button>
          </Link>
        </div>

        {/* Profile Link */}
        <div className="mt-8 text-center">
          <Link
            to={`/profile/${(window as any).__DJANGO_DATA__?.user?.id || ''}`}
            className="text-adv-orange hover:underline"
          >
            Go to My Profile
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
